import { Component, Inject, OnInit } from "@angular/core";

import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { forkJoin } from "rxjs";
import dayjs from "dayjs";

import { ApiService } from "../../../shared/services/api/api.service";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";

@Component({
  selector: "business-customer-details",
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent],
  templateUrl: "./customer-details.component.html",
  styleUrl: "./customer-details.component.scss",
})
export class BusinessCustomerDetailsComponent implements OnInit {
  isLoading = false;

  customer: any = null;

  /*
   * These are populated from the owner's
   * appointments endpoint.
   *
   * Do not rely on the customer-details
   * endpoint to contain appointment history.
   */
  customerAppointments: any[] = [];
  completedCustomerAppointments: any[] = [];
  customerServiceHistory: any[] = [];

  activeTab: "overview" | "visits" | "services" | "notes" | "insights" =
    "overview";

  tabs = [
    {
      key: "overview",
      label: "Overview",
    },
    {
      key: "visits",
      label: "Past Visits",
    },
    {
      key: "services",
      label: "Services",
    },
    {
      key: "notes",
      label: "Notes",
    },
    {
      key: "insights",
      label: "Insights",
    },
  ] as const;

  noteDraft = "";

  constructor(
    private apiService: ApiService,

    private dialogRef: MatDialogRef<BusinessCustomerDetailsComponent>,

    @Inject(MAT_DIALOG_DATA)
    public data: any,
  ) {}

  ngOnInit(): void {
    this.loadCustomer();
  }

  /*
   * =========================================================
   * LOAD CUSTOMER + APPOINTMENT HISTORY
   * =========================================================
   */

  loadCustomer(): void {
    const customerId = this.resolveDialogCustomerId();

    if (!customerId) {
      console.error(
        "Customer details opened without a customer id:",
        this.data,
      );

      return;
    }

    this.isLoading = true;

    forkJoin({
      customer: this.apiService.request<any>(
        API_ENDPOINTS.owner.customers.details(customerId),
        "GET",
      ),

      appointments: this.apiService.request<any>(
        API_ENDPOINTS.owner.appointments.list,
        "GET",
      ),
    }).subscribe({
      next: ({ customer, appointments }) => {
        /*
         * -----------------------------------------
         * Customer profile
         * -----------------------------------------
         */

        this.customer =
          customer?.customer ??
          customer?.data?.customer ??
          customer?.data ??
          customer ??
          null;

        this.noteDraft =
          this.customer?.notes ?? this.customer?.internal_notes ?? "";

        /*
         * -----------------------------------------
         * Appointment list
         * -----------------------------------------
         */

        const allAppointments = this.extractAppointments(appointments);

        this.customerAppointments = allAppointments
          .filter((appointment: any) =>
            this.appointmentBelongsToCustomer(appointment, customerId),
          )
          .sort(
            (a: any, b: any) =>
              this.getAppointmentTimestamp(b) - this.getAppointmentTimestamp(a),
          );

        /*
         * Past Visits should represent appointments
         * that actually happened/completed.
         */
        this.completedCustomerAppointments = this.customerAppointments.filter(
          (appointment: any) => this.isCompletedVisit(appointment),
        );

        /*
         * Build the Services tab from actual
         * AppointmentItem / service data.
         */
        this.customerServiceHistory = this.buildServiceHistory(
          this.completedCustomerAppointments,
        );

        this.isLoading = false;
      },

      error: (error: any) => {
        console.error("Failed to load customer details/history:", error);

        this.isLoading = false;
      },
    });
  }

  /*
   * =========================================================
   * RESPONSE NORMALIZATION
   * =========================================================
   */

  private extractAppointments(response: any): any[] {
    const possibleValues = [
      response?.results,
      response?.appointments,

      response?.data?.results,
      response?.data?.appointments,
      response?.data,

      response,
    ];

    for (const value of possibleValues) {
      if (Array.isArray(value)) {
        return value;
      }
    }

    return [];
  }

  private resolveDialogCustomerId(): string | null {
    const value =
      this.data?.customer_id ??
      this.data?.customerId ??
      this.data?.user_id ??
      this.data?.userId ??
      this.data?.id ??
      null;

    return value ? String(value) : null;
  }

  /*
   * =========================================================
   * CUSTOMER MATCHING
   * =========================================================
   */

  private appointmentBelongsToCustomer(
    appointment: any,
    customerId: string,
  ): boolean {
    const wantedId = this.normalizeId(customerId);

    const candidateIds = [
      appointment?.customer_id,
      appointment?.customerId,

      appointment?.customer?.customer_id,
      appointment?.customer?.customerId,

      appointment?.customer?.user_id,
      appointment?.customer?.userId,

      appointment?.customer?.id,

      appointment?.customer_user_id,

      appointment?.user_id,
    ]
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map((value) => this.normalizeId(value));

    if (candidateIds.includes(wantedId)) {
      return true;
    }

    /*
     * Some customer records are CRM records
     * while Appointment.customer points directly
     * at the Users table.
     *
     * Email gives us a safe secondary match when
     * the two identifiers differ.
     */
    const customerEmail = this.normalizeEmail(
      this.customer?.email ?? this.data?.email,
    );

    const appointmentEmail = this.normalizeEmail(
      appointment?.customer_email ?? appointment?.customer?.email,
    );

    if (
      customerEmail &&
      appointmentEmail &&
      customerEmail === appointmentEmail
    ) {
      return true;
    }

    return false;
  }

  private normalizeId(value: any): string {
    return String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/-/g, "");
  }

  private normalizeEmail(value: any): string {
    return String(value ?? "")
      .trim()
      .toLowerCase();
  }

  /*
   * =========================================================
   * PAST VISITS
   * =========================================================
   */

  private isCompletedVisit(appointment: any): boolean {
    const status = String(appointment?.status ?? "")
      .trim()
      .toLowerCase();

    /*
     * A completed appointment is definitely
     * a historical visit.
     */
    if (status === "completed" || status === "complete") {
      return true;
    }

    /*
     * Do NOT show cancelled/no-show appointments
     * as completed customer visits.
     */
    if (
      status === "cancelled" ||
      status === "canceled" ||
      status === "no_show"
    ) {
      return false;
    }

    /*
     * Historical appointments from older data may
     * not have been migrated to "completed".
     *
     * If their end time is already in the past,
     * treat them as a past visit unless explicitly
     * cancelled/no-show.
     */
    const endValue =
      appointment?.end_time ??
      appointment?.endTime ??
      appointment?.start_time ??
      appointment?.startTime;

    if (endValue && dayjs(endValue).isValid()) {
      return dayjs(endValue).isBefore(dayjs());
    }

    return false;
  }

  private getAppointmentTimestamp(appointment: any): number {
    const value =
      appointment?.start_time ??
      appointment?.startTime ??
      appointment?.date ??
      appointment?.created_at;

    if (!value || !dayjs(value).isValid()) {
      return 0;
    }

    return dayjs(value).valueOf();
  }

  /*
   * =========================================================
   * SERVICE HISTORY
   * =========================================================
   */

  private buildServiceHistory(appointments: any[]): any[] {
    const services = new Map<
      string,
      {
        service_id: string | null;
        name: string;
        service_name: string;
        count: number;
        total_bookings: number;
        last_booked: string | null;
        total_spend: number;
      }
    >();

    for (const appointment of appointments) {
      const items = this.extractAppointmentItems(appointment);

      for (const item of items) {
        const service = item?.service ?? item;

        const serviceId =
          service?.service_id ??
          service?.serviceId ??
          item?.service_id ??
          item?.serviceId ??
          null;

        const serviceName =
          service?.name ??
          service?.service_name ??
          service?.serviceName ??
          item?.service_name ??
          item?.serviceName ??
          item?.title ??
          null;

        if (!serviceName) {
          continue;
        }

        const key = serviceId
          ? this.normalizeId(serviceId)
          : serviceName.trim().toLowerCase();

        const appointmentDate =
          appointment?.start_time ??
          appointment?.startTime ??
          appointment?.date ??
          null;

        const price = this.toNumber(
          item?.price_snapshot ?? item?.price ?? service?.price ?? 0,
        );

        const existing = services.get(key);

        if (!existing) {
          services.set(key, {
            service_id: serviceId ? String(serviceId) : null,

            name: serviceName,

            service_name: serviceName,

            count: 1,

            total_bookings: 1,

            last_booked: appointmentDate,

            total_spend: price,
          });

          continue;
        }

        existing.count += 1;

        existing.total_bookings = existing.count;

        existing.total_spend += price;

        if (
          appointmentDate &&
          (!existing.last_booked ||
            dayjs(appointmentDate).isAfter(dayjs(existing.last_booked)))
        ) {
          existing.last_booked = appointmentDate;
        }
      }
    }

    return Array.from(services.values()).sort((a, b) => {
      /*
       * Most frequently booked service first.
       */
      if (b.count !== a.count) {
        return b.count - a.count;
      }

      const bDate =
        b.last_booked && dayjs(b.last_booked).isValid()
          ? dayjs(b.last_booked).valueOf()
          : 0;

      const aDate =
        a.last_booked && dayjs(a.last_booked).isValid()
          ? dayjs(a.last_booked).valueOf()
          : 0;

      return bDate - aDate;
    });
  }

  private extractAppointmentItems(appointment: any): any[] {
    const candidates = [
      appointment?.items,
      appointment?.appointment_items,
      appointment?.appointmentItems,
      appointment?.services,
    ];

    for (const value of candidates) {
      if (Array.isArray(value)) {
        return value;
      }
    }

    return [];
  }

  /*
   * =========================================================
   * DIALOG
   * =========================================================
   */

  close(): void {
    this.dialogRef.close();
  }

  setTab(tab: typeof this.activeTab): void {
    this.activeTab = tab;
  }

  /*
   * =========================================================
   * NOTES
   * =========================================================
   */

  saveNotes(): void {
    const customerId =
      this.customer?.customer_id ??
      this.customer?.customerId ??
      this.resolveDialogCustomerId();

    if (!customerId) {
      return;
    }

    const payload = {
      notes: this.noteDraft,
      internal_notes: this.noteDraft,
    };

    this.isLoading = true;

    this.apiService
      .request(
        API_ENDPOINTS.owner.customers.update(String(customerId)),
        "PATCH",
        payload,
      )
      .subscribe({
        next: (response: any) => {
          const updatedCustomer =
            response?.customer ??
            response?.data?.customer ??
            response?.data ??
            response ??
            {};

          this.customer = {
            ...this.customer,
            ...updatedCustomer,

            notes: this.noteDraft,

            internal_notes: this.noteDraft,
          };

          this.isLoading = false;
        },

        error: (error: any) => {
          console.error("Unable to save customer notes:", error);

          this.isLoading = false;
        },
      });
  }

  /*
   * =========================================================
   * CUSTOMER DISPLAY
   * =========================================================
   */

  get fullName(): string {
    return (
      this.customer?.full_name ||
      this.customer?.fullName ||
      [
        this.customer?.first_name ?? this.customer?.firstName,

        this.customer?.last_name ?? this.customer?.lastName,
      ]
        .filter(Boolean)
        .join(" ") ||
      "Customer"
    );
  }

  get initials(): string {
    return this.fullName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((value) => value[0])
      .join("")
      .toUpperCase();
  }

  /*
   * =========================================================
   * CRM METRICS
   * =========================================================
   */

  get lifetimeValue(): number {
    /*
     * Prefer an API-calculated lifetime value
     * when available.
     *
     * Otherwise calculate it from completed
     * appointment history.
     */
    const apiValue =
      this.customer?.lifetime_value ?? this.customer?.lifetimeValue;

    if (apiValue !== null && apiValue !== undefined && apiValue !== "") {
      return this.toNumber(apiValue);
    }

    return this.completedCustomerAppointments.reduce(
      (sum: number, appointment: any) =>
        sum +
        this.toNumber(appointment?.total_price ?? appointment?.total ?? 0),
      0,
    );
  }

  get totalVisits(): number {
    /*
     * Appointment history is the more useful
     * source now that this component loads it.
     */
    if (this.completedCustomerAppointments.length) {
      return this.completedCustomerAppointments.length;
    }

    return this.toNumber(
      this.customer?.total_visits ?? this.customer?.totalVisits,
    );
  }

  get averageSpend(): number {
    if (!this.totalVisits) {
      return 0;
    }

    return this.lifetimeValue / this.totalVisits;
  }

  get visitFrequency(): string {
    const visits = this.totalVisits;

    if (visits >= 12) {
      return "Very frequent";
    }

    if (visits >= 6) {
      return "Frequent";
    }

    if (visits >= 2) {
      return "Occasional";
    }

    if (visits === 1) {
      return "New customer";
    }

    return "No visit history";
  }

  get lastVisitDate(): string | null {
    const mostRecent = this.completedCustomerAppointments[0];

    return (
      mostRecent?.start_time ??
      mostRecent?.startTime ??
      this.customer?.last_visit ??
      this.customer?.lastVisit ??
      null
    );
  }

  get riskStatus(): string {
    const lastVisit = this.lastVisitDate;

    if (!lastVisit) {
      return "Needs first completed visit";
    }

    const days = dayjs().diff(dayjs(lastVisit), "day");

    if (days >= 120) {
      return "High reactivation risk";
    }

    if (days >= 90) {
      return "At risk";
    }

    if (days >= 45) {
      return "Follow-up recommended";
    }

    return "Healthy";
  }

  get loyaltyStatus(): string {
    if (this.lifetimeValue >= 1000 || this.totalVisits >= 12) {
      return "VIP";
    }

    if (this.lifetimeValue >= 500 || this.totalVisits >= 6) {
      return "Loyal";
    }

    if (this.totalVisits >= 2) {
      return "Repeat";
    }

    return "New";
  }

  /*
   * =========================================================
   * TABS
   * =========================================================
   */

  get pastVisits(): any[] {
    return this.completedCustomerAppointments;
  }

  get serviceHistory(): any[] {
    return this.customerServiceHistory;
  }

  get preferredServices(): string[] {
    /*
     * Prefer actual historical usage.
     */
    if (this.customerServiceHistory.length) {
      return this.customerServiceHistory
        .slice(0, 5)
        .map((service: any) => service.name);
    }

    /*
     * Fall back to explicit CRM preferences
     * if there is no appointment history.
     */
    const values =
      this.customer?.preferred_services ??
      this.customer?.favorite_services ??
      this.customer?.recent_services ??
      [];

    if (!Array.isArray(values) || !values.length) {
      return ["No preferences recorded"];
    }

    return values.map((item: any) => item?.name ?? item?.service_name ?? item);
  }

  /*
   * =========================================================
   * INSIGHTS
   * =========================================================
   */

  getFollowUpSuggestion(): string {
    if (!this.customer?.birthday) {
      return (
        "Add birthday to support birthday rewards " +
        "and personalized promotions."
      );
    }

    if (this.riskStatus.toLowerCase().includes("risk")) {
      return (
        "Send a rebooking reminder or targeted " +
        "promotion to reactivate this customer."
      );
    }

    if (this.loyaltyStatus === "VIP") {
      return (
        "Consider VIP treatment, priority booking, " + "or a loyalty reward."
      );
    }

    if (!this.totalVisits) {
      return (
        "Customer has no completed visit yet. " +
        "Send a welcome message after first booking."
      );
    }

    return (
      "Relationship looks healthy. Keep notes and " +
      "preferences updated after each visit."
    );
  }

  /*
   * =========================================================
   * FORMATTING
   * =========================================================
   */

  formatDate(value: string, format = "MMM D, YYYY"): string {
    if (!value || !dayjs(value).isValid()) {
      return "Not added";
    }

    return dayjs(value).format(format);
  }

  toMoney(value: any): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(this.toNumber(value));
  }

  toNumber(value: any): number {
    const numberValue = Number(value);

    return Number.isFinite(numberValue) ? numberValue : 0;
  }
}
